import { create } from 'zustand';

/** UI store state interface */
interface UIState {
  /** Whether the mobile navigation menu is open */
  isMobileMenuOpen: boolean;
  /** Toggle the mobile menu open/closed */
  toggleMobileMenu: () => void;
  /** Close the mobile menu */
  closeMobileMenu: () => void;

  /** Whether the inquiry modal is open */
  isInquiryModalOpen: boolean;
  /** The plan ID associated with the current inquiry (if any) */
  currentInquiryPlanId: string | null;
  /** Open the inquiry modal, optionally with a specific plan */
  openInquiryModal: (planId?: string | null) => void;
  /** Close the inquiry modal */
  closeInquiryModal: () => void;

  /** Current theme template ID */
  themeTemplate: 'A' | 'B' | 'C';
  /** Switch the theme template */
  setThemeTemplate: (t: 'A' | 'B' | 'C') => void;
}

/**
 * UI state store for managing global UI interactions.
 * Includes mobile menu toggle, inquiry modal state, and theme template switching.
 */
export const useUIStore = create<UIState>()((set) => ({
  isMobileMenuOpen: false,

  toggleMobileMenu: () => {
    set((state) => ({ isMobileMenuOpen: !state.isMobileMenuOpen }));
  },

  closeMobileMenu: () => {
    set({ isMobileMenuOpen: false });
  },

  isInquiryModalOpen: false,
  currentInquiryPlanId: null,

  openInquiryModal: (planId: string | null = null) => {
    set({
      isInquiryModalOpen: true,
      currentInquiryPlanId: planId ?? null,
    });
  },

  closeInquiryModal: () => {
    set({
      isInquiryModalOpen: false,
      currentInquiryPlanId: null,
    });
  },

  /** Theme template — read from localStorage on init */
  themeTemplate: (typeof window !== 'undefined'
    ? (localStorage.getItem('tj_theme_template') as 'A' | 'B' | 'C') || 'A'
    : 'A'),

  setThemeTemplate: (t) => {
    localStorage.setItem('tj_theme_template', t);
    set({ themeTemplate: t });
  },
}));
